import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Form, Input, Button, Skeleton } from "antd";
import toast from "react-hot-toast";
import Edit from "../../../assets/Edit";
import {
  getEmailSettingsApi,
  UpdateEmailSettingsApi,
} from "../../../api/request/setting/setting";
import LoadableButton from "../../button/LoadableButton";
import GlobalTooltip from "../../common/GlobalTooltip";

const EmailSettings = () => {
  const userDetails = useSelector((state) => state.user.userDetails);
  const [form] = Form.useForm();
  const [settingsData, setSettingsData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);

  const isAdmin = userDetails?.role === "admin";

  const fields = [
    { name: "username", label: "Mail Username", type: "text" },
    { name: "password", label: "Mail Password", type: "text" },
    { name: "from_name", label: "Mail From Name", type: "text" },
    { name: "from_address", label: "Mail From Address", type: "email" },
    { name: "host", label: "Mail Host", type: "text" },
    { name: "port", label: "Mail Port", type: "number" },
    { name: "encryption", label: "Mail Encryption", type: "text" },
  ];

  const validation = {
    email: [{ type: "email", message: "Enter a valid email" }],
  };

  const getSiteSettings = async () => {
    try {
      setIsLoading(true);
      const { data } = await getEmailSettingsApi();
      if (data?.status) {
        setSettingsData(data?.data);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSiteSettings();
  }, []);

  const handleSave = async (values) => {
    setIsUpdateLoading(true);
    try {
      const { data } = await UpdateEmailSettingsApi(values);
      if (data?.status) {
        setIsEditing(false);
        toast.success(data?.message);
        setSettingsData({ ...settingsData, ...data?.data });
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsUpdateLoading(false);
    }
  };

  return (
    <div className="h-full border rounded-lg p-4">
      <div className="flex justify-between border-b pb-3">
        <p className="title">Email Settings</p>
        {isAdmin ? (
          <GlobalTooltip title={"Edit Email Settings"}>
            <button onClick={() => setIsEditing(!isEditing)}>
              <Edit />
            </button>
          </GlobalTooltip>
        ) : null}
      </div>

      <div className="pt-4">
        {isEditing ? (
          <Form
            form={form}
            layout="vertical"
            initialValues={settingsData}
            onFinish={handleSave}
          >
            <div className="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-x-5">
              {fields.map((field) => (
                <Form.Item
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  rules={validation[field.type] || []}
                >
                  <Input
                    type={field.type}
                    placeholder={`Enter ${field.label}`}
                  />
                </Form.Item>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <LoadableButton
                type="submit"
                lable="Update"
                loadingLable="Updating..."
                isLoading={isUpdateLoading}
              />
              <button
                className="btn-outlined"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
            </div>
          </Form>
        ) : (
          <div className="space-y-4">
            {isLoading
              ? fields.map((field) => (
                  <div key={field.name} className="flex gap-3">
                    <Skeleton.Input active size="small" />
                    <Skeleton.Input
                      active
                      size="small"
                      className="!w-[300px]"
                    />
                  </div>
                ))
              : fields.map((field) => {
                  const value = settingsData[field?.name];
                  return (
                    <div key={field.name} className="flex gap-3">
                      <span className="font-medium">{field.label}:</span>
                      {field.type === "url" && value ? (
                        <a
                          href={value}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500"
                        >
                          {value}
                        </a>
                      ) : (
                        value || "--"
                      )}
                    </div>
                  );
                })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSettings;
